import { t } from "../locale";
import { Html } from "./html";
import { MaterialDesign } from "./material_design";

/**
 * @param {...(Node|string)} children
 */
function box(...children) {
	const box = document.createElement("div");
	box.className = "box";
	box.append(...children);
	return box;
}

/**
 * @param  {...HTMLAnchorElement} children
 */
function breadcrumb(...children) {
	children[children.length - 1].classList.add("is-active");

	const breadcrumbs = document.createElement("nav");
	breadcrumbs.className = "breadcrumb";
	breadcrumbs.append(Html.unorderedList(...children).element);
	return breadcrumbs;
}

/**
 * @typedef {Object} ButtonConfig
 * @property {boolean} [disabled]
 * @property {("is-danger"|"is-fullwidth"|"is-light"|"is-primary"|"is-small")[]} [styles]
 * @property {(event: MouseEvent) => void} [onClick]
 *
 * @param {ButtonConfig} cfg
 * @param {...(Node|string)} children
 */
function button(cfg, ...children) {
	const button = document.createElement("button");
	button.className = "button";
	button.append(...children);
	if (cfg.disabled) button.disabled = cfg.disabled;
	if (cfg.styles)
		cfg.styles.forEach((style) => {
			button.classList.add(style);
		});
	if (cfg.onClick) button.onclick = cfg.onClick;

	return button;
}

/**
 * @param  {...HTMLButtonElement} buttons
 */
function buttons(...buttons) {
	const div = document.createElement("div");
	div.className = "buttons";
	div.append(...buttons);
	return div;
}

/**
 * @param {...HTMLElement} children
 */
function card(...children) {
	const card = document.createElement("div");
	card.className = "card";
	card.append(...children);
	return card;
}

/**
 * @param  {...HTMLElement} children
 */
function cardContent(...children) {
	const content = document.createElement("div");
	content.className = "card-content";
	content.append(...children);
	return content;
}

/**
 * @typedef {Object} CardFooterConfig
 * @property {DeviceType} [color]
 *
 * @param {CardFooterConfig} cfg
 * @param  {...HTMLAnchorElement} children
 */
function cardFooter(cfg, ...children) {
	const footer = document.createElement("footer");
	footer.className = "card-footer";
	footer.append(...children);
	children.forEach((a) => {
		a.classList.add("card-footer-item");
	});
	if (cfg.color) footer.classList.add(cfg.color);
	return footer;
}

/**
 * @typedef {Object} CardHeaderConfig
 * @property {DeviceType} [color]
 * @property {(event: MouseEvent) => void} [onClick]
 *
 * @param {CardHeaderConfig} cfg
 * @param  {...HTMLElement} children
 * @returns
 */
function cardHeader(cfg, ...children) {
	const header = document.createElement("header");
	header.className = "card-header p-2";
	header.append(...children);
	if (cfg.color) header.classList.add(cfg.color);
	if (cfg.onClick) {
		header.classList.add("is-clickable");
		header.onclick = cfg.onClick;
	}
	return header;
}

/**
 * @param {HTMLSpanElement} icon
 */
function cardHeaderIcon(icon) {
	const button = document.createElement("button");
	button.className = "card-header-icon";
	button.append(icon);
	return button;
}

/**
 * @param {string} text
 */
function cardHeaderTitle(text) {
	const p = document.createElement("p");
	p.className = "card-header-title";
	p.append(text);
	return p;
}

/**
 * @typedef {Object} CheckboxConfig
 * @property {string} [text]
 * @property {boolean} [checked]
 * @property {boolean} [disabled]
 *
 * @param {CheckboxConfig} cfg
 */
function checkbox(cfg) {
	const checkbox = document.createElement("input");
	checkbox.type = "checkbox";
	if (cfg.checked) checkbox.checked = cfg.checked;
	if (cfg.disabled) checkbox.classList.add("is-disabled");

	const label = document.createElement("label");
	label.className = "checkbox";
	label.append(checkbox);
	if (cfg.text) label.append(` ${cfg.text}`);

	return {
		element: label,
		isChecked: () => checkbox.checked,
		/** @type {(checked: boolean) => void} */
		setChecked: (checked) => {
			checkbox.checked = checked;
		},
	};
}

/**
 *
 * @param {...HTMLLabelElement} children
 */
function checkboxes(...children) {
	const div = document.createElement("div");
	div.className = "checkboxes";
	div.append(...children);
	return div;
}

/**
 * @typedef {Object} ColumnConfig
 * @property {ColumnSize[]} [classList]
 *
 * @param {ColumnConfig} cfg
 * @param {...Node} children
 */
function column(cfg, ...children) {
	const column = document.createElement("div");
	column.className = "column";
	column.append(...children);
	if (cfg.classList)
		cfg.classList.forEach((c) => {
			column.classList.add(c);
		});
	return column;
}

/**
 * @typedef {Object} ColumnsConfig
 * @property {boolean} [isCentered]
 * @property {boolean} [isMobile]
 * @property {boolean} [isMultiline]
 *
 * @param {ColumnsConfig} cfg
 * @param {...HTMLDivElement} children
 */
function columns(cfg, ...children) {
	const columns = document.createElement("div");
	columns.className = "columns";
	columns.append(...children);
	if (cfg.isCentered) columns.classList.add("is-centered");
	if (cfg.isMobile) columns.classList.add("is-mobile");
	if (cfg.isMultiline) columns.classList.add("is-multiline");
	return columns;
}

/**
 *
 * @param  {...HTMLElement} children
 * @returns
 */
function container(...children) {
	const container = document.createElement("div");
	container.className = "container is-fluid";
	container.append(...children);
	return container;
}

/**
 * @typedef {Object} ControlConfig
 * @property {boolean} [hidden]
 * @property {("has-icons-left"|"has-icons-right"|"is-expanded")[]} [styles]
 *
 * @param {ControlConfig} cfg
 * @param {...Node} children
 */
function control(cfg, ...children) {
	const control = document.createElement("div");
	control.className = "control";
	control.append(...children);
	if (cfg.hidden) control.classList.add("is-hidden");
	if (cfg.styles)
		cfg.styles.forEach((style) => {
			control.classList.add(style);
		});
	return control;
}

/**
 * @typedef {Object} DateInputConfig
 * @property {(event: Event) => void} [onChange]
 *
 * @param {DateInputConfig} cfg
 */
function dateInput(cfg) {
	const input = document.createElement("input");
	input.type = "date";
	input.className = "input";
	input.value = new Date().toISOString().split("T").shift() || "";
	if (cfg.onChange) input.onchange = cfg.onChange;

	return {
		element: input,
		getDate: () => new Date(`${input.value}T00:00`),
	};
}

/**
 * @param {(event: MouseEvent) => void} onClick
 * @param {""|"is-small"|"is-medium"|"is-large"} [size=""]
 */
function deleteButton(onClick, size = "") {
	const button = document.createElement("button");
	button.className = "delete";
	button.onclick = onClick;
	if (size) button.classList.add(size);
	return button;
}

/**
 * @typedef {Object} FieldConfig
 * @property {("has-addons"|"has-addons-right"|"is-grouped"|"is-grouped-multiline"|"is-hidden")[]} [styles]
 *
 * @param {FieldConfig} cfg
 * @param {...Node} children
 */
function field(cfg, ...children) {
	const field = document.createElement("div");
	field.className = "field";
	if (cfg.styles)
		cfg.styles.forEach((style) => {
			field.classList.add(style);
		});
	field.append(...children);
	return field;
}

function fileInput() {
	const fileName = document.createElement("span");
	fileName.className = "file-name";

	const fileCta = document.createElement("span");
	fileCta.className = "file-cta";
	fileCta.append(
		MaterialDesign.icon({
			id: "upload",
		}),
	);

	const fileInput = document.createElement("input");
	fileInput.className = "file-input";
	fileInput.type = "file";
	fileInput.oninput = () => {
		if (!fileInput.files) return;
		if (fileInput.files.length < 1) return;
		fileName.textContent = fileInput.files[0].name;
	};

	const fileLabel = document.createElement("label");
	fileLabel.className = "file-label";
	fileLabel.append(fileInput, fileCta, fileName);

	const fileInputContainer = document.createElement("div");
	fileInputContainer.className = "file has-name is-fullwidth";
	fileInputContainer.append(fileLabel);

	const getBase64 = async () =>
		new Promise((resolve, reject) => {
			if (!fileInput.files) return;
			if (fileInput.files.length < 1) return;
			const reader = new FileReader();
			reader.onloadend = () => {
				if (reader.readyState !== FileReader.DONE) reject();
				else if (typeof reader.result !== "string") reject();
				else resolve(reader.result.split(",")[1]);
			};
			reader.onerror = reject;
			reader.readAsDataURL(fileInput.files[0]);
		});

	return {
		element: fileInputContainer,
		getBase64,
	};
}

/**
 * @param  {...Node} children
 */
function hero(...children) {
	const hero = document.createElement("section");
	hero.className = "hero is-fullheight-with-navbar";
	hero.append(...children);
	return hero;
}

/**
 * @param {string} [text=""]
 */
function help(text = "") {
	const p = document.createElement("p");
	p.className = "help";
	p.append(text);
	return p;
}

/**
 * @typedef {Object} InputConfig
 * @property {"email"|"password"|"tel"|"text"} type
 * @property {boolean} [isReadonly]
 * @property {(event: Event) => void} [onInput]
 * @property {string} [placeholder]
 * @property {("is-static")[]} [styles]
 * @property {string} [value]
 *
 * @param {InputConfig} cfg
 */
function input(cfg) {
	const input = document.createElement("input");
	input.className = "input";
	input.type = cfg.type;
	if (cfg.isReadonly) input.setAttribute("readonly", "");
	if (cfg.onInput) input.addEventListener("input", cfg.onInput);
	if (cfg.placeholder) input.placeholder = cfg.placeholder;
	if (cfg.styles)
		cfg.styles.forEach((style) => {
			input.classList.add(style);
		});
	if (cfg.value) input.value = cfg.value;

	/**
	 * @param {string} v
	 */
	const setValue = (v) => {
		input.value = v;
	};

	return {
		element: input,
		getValue: () => input.value,
		setValue,
	};
}

/**
 * @param {string} text
 */
function label(text) {
	const label = document.createElement("label");
	label.className = "label";
	label.textContent = text;
	return label;
}

/**
 * @param {...HTMLDivElement} children
 */
function level(...children) {
	const level = document.createElement("div");
	level.className = "level is-mobile";
	level.append(...children);
	return level;
}

/**
 * @param  {...HTMLElement} children
 */
function levelItem(...children) {
	const div = document.createElement("div");
	div.append(...children);

	const item = document.createElement("div");
	item.className = "level-item";
	item.append(div);
	return item;
}

/**
 * @param  {...HTMLElement} children
 */
function levelLeft(...children) {
	const left = document.createElement("div");
	left.className = "level-left";
	left.append(...children);
	return left;
}

/**
 * @param  {...HTMLElement} children
 */
function levelRight(...children) {
	const right = document.createElement("div");
	right.className = "level-right";
	right.append(...children);
	return right;
}

/**
 * @typedef {Object} MediaConfig
 * @property {HTMLElement} [image]
 * @property {HTMLElement[]} [content]
 * @property {HTMLElement[]} [right]
 *
 * @param {MediaConfig} cfg
 */
function media(cfg) {
	const media = document.createElement("div");
	media.className = "media";

	if (cfg.image) {
		const left = document.createElement("figure");
		left.className = "media-left";
		left.append(cfg.image);
		media.append(left);
	}

	if (cfg.content) {
		const content = document.createElement("div");
		content.className = "media-content";
		content.append(...cfg.content);
		media.append(content);
	}

	if (cfg.right) {
		const right = document.createElement("div");
		right.className = "media-right";
		right.append(...cfg.right);
		media.append(right);
	}

	return media;
}

function modalCard() {
	const closeModal = () => modal.classList.remove("is-active");

	const card = document.createElement("div");
	card.className = "modal-card";

	const background = document.createElement("div");
	background.className = "modal-background";
	background.onclick = closeModal;

	const modal = document.createElement("div");
	modal.className = "modal";
	modal.append(background, card);

	/**
	 * @typedef {Object} ModalCardConfig
	 * @property {string} header
	 * @property {HTMLElement[]} body
	 * @property {HTMLButtonElement[]} footer
	 *
	 * @param {ModalCardConfig} cfg
	 */
	const open = (cfg) => {
		const title = document.createElement("p");
		title.className = "modal-card-title";
		title.append(cfg.header);

		const header = document.createElement("header");
		header.className = "modal-card-head";
		header.append(title, deleteButton(closeModal));

		const body = document.createElement("section");
		body.className = "modal-card-body";
		body.append(...cfg.body);

		const footer = document.createElement("footer");
		footer.className = "modal-card-foot";
		footer.append(buttons(...cfg.footer));

		card.replaceChildren(header, body, footer);
		modal.classList.add("is-active");
	};

	return {
		element: modal,
		open,
		close: closeModal,
	};
}

/**
 * @typedef {Object} NavbarConfig
 * @property {string} theme
 * @property {(HTMLAnchorElement|HTMLDivElement)[]} brand
 * @property {(HTMLAnchorElement|HTMLDivElement)[]} start
 * @property {(HTMLAnchorElement|HTMLDivElement)[]} end
 */

/**
 * @param {NavbarConfig} cfg
 */
function navbar(cfg) {
	const navbarStart = document.createElement("div");
	navbarStart.className = "navbar-start";
	navbarStart.append(...cfg.start);

	const navbarEnd = document.createElement("div");
	navbarEnd.className = "navbar-end";
	navbarEnd.append(...cfg.end);

	// -----------------------------------------------

	const navbarMenu = document.createElement("div");
	navbarMenu.className = "navbar-menu";
	navbarMenu.append(navbarStart, navbarEnd);

	// -----------------------------------------------

	const navbarBurger = document.createElement("a");
	navbarBurger.className = "navbar-burger";
	navbarBurger.append(navbarItem({}, t.menu));
	navbarBurger.onclick = () => {
		navbarBurger.classList.toggle("is-active");
		navbarMenu.classList.toggle("is-active");
	};

	const navbarBrand = document.createElement("div");
	navbarBrand.className = "navbar-brand";
	navbarBrand.append(...cfg.brand, navbarBurger);

	// -----------------------------------------------

	const navbar = document.createElement("nav");
	navbar.className = `navbar is-fixed-top ${cfg.theme}`;
	navbar.append(navbarBrand, navbarMenu);
	return navbar;
}

/**
 * @param {string} text
 * @param  {...HTMLAnchorElement} items
 */
function navbarDropdown(text, ...items) {
	const link = document.createElement("a");
	link.className = "navbar-link";
	link.textContent = text;

	const dropdown = document.createElement("div");
	dropdown.className = "navbar-dropdown";
	dropdown.append(...items);

	const item = document.createElement("div");
	item.className = "navbar-item has-dropdown is-hoverable";
	item.append(link, dropdown);
	return item;
}

/**
 * @typedef {Object} NavbarAnchorItemConfig
 * @property {boolean} [active]
 * @property {boolean} [hidden]
 * @property {string} [href]
 * @property {(event: MouseEvent) => void} [onClick]
 * @property {"_blank"} [target]
 *
 * @param {NavbarAnchorItemConfig} cfg
 * @param  {...(HTMLElement|string)} children
 */
function navbarItem(cfg, ...children) {
	const item = document.createElement("a");
	item.className = "navbar-item";
	item.append(...children);
	if (cfg.active) item.classList.add("is-active");
	if (cfg.hidden) item.classList.add("is-hidden");
	if (cfg.href) item.href = cfg.href;
	if (cfg.onClick) item.onclick = cfg.onClick;
	if (cfg.target) item.target = cfg.target;
	return item;
}

/**
 * @param {string} text
 * @param {string} value
 * @param {boolean} [selected=false]
 */
function option(text, value, selected = false) {
	const option = document.createElement("option");
	option.textContent = text;
	option.value = value;
	option.selected = selected;
	return option;
}

/**
 * @typedef {Object} SectionConfig
 * @property {boolean} [hidden]
 *
 * @param {SectionConfig} cfg
 * @param  {...HTMLElement} children
 */
function section(cfg, ...children) {
	const section = document.createElement("section");
	section.className = "section";
	section.append(...children);
	if (cfg.hidden) section.classList.add("is-hidden");
	return section;
}

/**
 * @typedef {Object} SelectConfig
 * @property {HTMLOptionElement[]} options
 * @property {boolean} [disabled]
 * @property {("is-fullwidth")[]} [styles]
 * @property {() => void} [onInput]
 *
 * @param {SelectConfig} cfg
 */
function select(cfg) {
	const select = document.createElement("select");
	select.append(...cfg.options);
	if (cfg.disabled) select.disabled = cfg.disabled;
	if (cfg.onInput) select.oninput = cfg.onInput;

	const container = document.createElement("div");
	container.className = "select";
	container.append(select);
	if (cfg.styles)
		cfg.styles.forEach((style) => {
			container.classList.add(style);
		});

	/**
	 * @param {string} v
	 */
	function setValue(v) {
		select.value = v;
	}

	return {
		element: container,
		getValue: () => select.value,
		setValue,
	};
}

/**
 * @param {string} text
 * @param {"is-1"|"is-2"|"is-3"|"is-4"|"is-5"|"is-6"} [size="is-3"]
 */
function subtitle(text, size = "is-5") {
	const subtitle = document.createElement("p");
	subtitle.className = `subtitle ${size}`;
	subtitle.textContent = text;
	return subtitle;
}

/**
 * @typedef {Object} TableConfig
 * @property {("is-fullwidth"|"is-striped")[]} [classList]
 * @property {HTMLTableRowElement} [header]
 * @property {HTMLTableRowElement[]} [body]
 * @property {HTMLTableRowElement} [footer]
 *
 * @param {TableConfig} cfg
 */
function table(cfg) {
	const table = document.createElement("table");
	table.className = "table";
	if (cfg.classList)
		cfg.classList.forEach((c) => {
			table.classList.add(c);
		});

	if (cfg.header) {
		const header = document.createElement("thead");
		header.append(cfg.header);
		table.append(header);
	}

	if (cfg.body) {
		const body = document.createElement("tbody");
		body.append(...cfg.body);
		table.append(body);
	}

	if (cfg.footer) {
		const footer = document.createElement("tfoot");
		footer.append(cfg.footer);
		table.append(footer);
	}

	return table;
}

/**
 * @param {Node|string} content
 */
function tableDataCell(content) {
	const cell = document.createElement("td");
	cell.append(content);
	return cell;
}

/**
 * @param {Node|string} content
 */
function tableHeaderCell(content) {
	const cell = document.createElement("th");
	cell.append(content);
	return cell;
}

/**
 * @typedef {Object} TableRowConfig
 * @property {(event: MouseEvent) => void} [onClick]
 *
 * @param {TableRowConfig} cfg
 * @param  {...HTMLTableCellElement} cells
 */
function tableRow(cfg, ...cells) {
	const row = document.createElement("tr");
	row.append(...cells);
	if (cfg.onClick) {
		row.classList.add("is-clickable");
		row.onclick = cfg.onClick;
	}
	return row;
}

/**
 * @typedef {Object} TagConfig
 * @property {HTMLSpanElement|string} content
 * @property {""|"is-primary"} [color]
 * @property {(event: MouseEvent) => void} [onClick]
 *
 * @param {TagConfig} cfg
 */
function tag(cfg) {
	const tag = document.createElement("span");
	tag.classList.add("tag");
	tag.append(cfg.content);
	if (cfg.color) tag.classList.add(cfg.color);
	if (cfg.onClick) tag.onclick = cfg.onClick;
	return tag;
}

/**
 * @param {TagConfig} left
 * @param {TagConfig} right
 */
function tagWithAddon(left, right) {
	const tags = document.createElement("div");
	tags.className = "tags has-addons";
	tags.append(tag(left), tag(right));
	return tags;
}

function timeInput() {
	const input = document.createElement("input");
	input.type = "time";
	input.className = "input";

	const getUtc = () => {
		const [localHours, localMinutes] = input.value.split(":");
		const localDate = new Date();
		localDate.setHours(+localHours, +localMinutes);
		return localDate.toISOString().substring(11, 16);
	};

	return {
		element: input,
		getUtc,
		getLocal: () => input.value,
	};
}

/**
 * @param {string} text
 * @param {"is-1"|"is-2"|"is-3"|"is-4"|"is-5"|"is-6"} [size="is-3"]
 */
function title(text, size = "is-3") {
	const title = document.createElement("p");
	title.className = `title ${size}`;
	title.textContent = text;
	return title;
}

export const Bulma = {
	box,
	breadcrumb,
	button,
	buttons,
	card,
	cardContent,
	cardFooter,
	cardHeader,
	cardHeaderIcon,
	cardHeaderTitle,
	checkbox,
	checkboxes,
	column,
	columns,
	container,
	control,
	dateInput,
	field,
	fileInput,
	input,
	hero,
	help,
	label,
	level,
	levelItem,
	levelLeft,
	levelRight,
	media,
	modalCard,
	navbar,
	navbarDropdown,
	navbarItem,
	option,
	section,
	select,
	subtitle,
	table,
	tableDataCell,
	tableHeaderCell,
	tableRow,
	tag,
	tagWithAddon,
	timeInput,
	title,
};
